import { useEffect } from "react";

import { Badge, Button } from "@similarweb/ui-components";

import WidgetBanner from "app/components/WidgetBanner/WidgetBanner";
import { translate } from "app/filters/i18nFilter";
import { RT_PATH } from "app/pages/activation/components/RankWidget/config";
import { widgetKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { Domain } from "app/pages/activation/components/RankWidget/Header/Domain";
import { getRTDates } from "app/pages/activation/components/RankWidget/helpers/getRTDates";
import { openRankTrackerPage } from "app/pages/activation/components/RankWidget/helpers/openRankTrackerPage";
import { VIEW_BUTTON_TEXT, widgetTexts } from "app/pages/activation/components/RankWidget/texts";
import Duration from "app/pages/activation/components/Shared/Duration/Duration";
import { useRTDomainIcon } from "app/pages/activation/hooks/rankTracker/useRTDomainIcon";
import * as SCHome from "app/pages/activation/modules/Project/styles";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { RankTrackerCampaign } from "app/pages/activation/types/api";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { Country } from "./Country";
import { Source } from "./Source";
import * as SC from "./styles";

type HeaderSchema = {
    isScraping?: boolean;
    RTCampaign?: RankTrackerCampaign;
};

export const Header = ({ isScraping, RTCampaign }: HeaderSchema) => {
    const { duration, range } = getRTDates(RTCampaign);
    const domain = RTCampaign?.site || "";
    const { rankTracker } = activationTracking;

    const domainImageUrl = useRTDomainIcon(domain);

    const onClickViewButton = () => {
        if (!RTCampaign) {
            return;
        }

        rankTracker.trackClickViewCampaign();

        openRankTrackerPage({
            campaignId: RTCampaign.id,
            scrapingConfigurations: RTCampaign.scrapingConfigurations,
            site: RTCampaign.site,
            duration,
        });
    };

    const source = RTCampaign?.scrapingConfigurations?.find(({ device }) => device === DEVICES_TYPES_FOR_API.DESKTOP)
        ? DEVICES_TYPES_FOR_API.DESKTOP
        : DEVICES_TYPES_FOR_API.MOBILE;

    // need for init data and correct routing to RT
    useEffect(() => {
        proNavigator.lazyLoadIfNeeded(RT_PATH);
    }, []);

    const viewButton = RTCampaign ? (
        <Button
            type="flat"
            dataAutomation={widgetKeys.viewButton}
            onClick={onClickViewButton}
        >
            {translate(VIEW_BUTTON_TEXT)}
        </Button>
    ) : null;

    return (
        <SCHome.WidgetHeader data-automation={widgetKeys.header}>
            <SCHome.WidgetTitle>{translate(widgetTexts.title)}</SCHome.WidgetTitle>

            {isScraping ? (
                <>
                    <SC.BadgeContainer data-automation={widgetKeys.scrapingBadge}>
                        <Badge type="new">{translate(widgetTexts.scrapingBadge)}</Badge>
                    </SC.BadgeContainer>

                    <SC.ToRightContainer>
                        <WidgetBanner
                            title={translate(widgetTexts.bannerText)}
                            dataAutomation={widgetKeys.banner}
                        />
                        {viewButton}
                    </SC.ToRightContainer>
                </>
            ) : (
                <>
                    {domain ? (
                        <Domain
                            name={domain}
                            imgUrl={domainImageUrl}
                            dataAutomation={widgetKeys.domain}
                        />
                    ) : null}

                    {RTCampaign?.firstScrapeTime ? <Duration duration={range} /> : null}

                    {RTCampaign?.scrapingConfigurations?.[0]?.geoLocationCountry ? (
                        <Country
                            countryCode={RTCampaign.scrapingConfigurations[0].geoLocationCountry}
                            dataAutomation={widgetKeys.country}
                        />
                    ) : null}

                    {RTCampaign ? (
                        <Source
                            source={source}
                            dataAutomation={widgetKeys.source}
                        />
                    ) : null}

                    {viewButton ? <SC.ToRightContainer>{viewButton}</SC.ToRightContainer> : null}
                </>
            )}
        </SCHome.WidgetHeader>
    );
};
